import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Logger } from '@patsnap/tahiti-core';
import { HeaderService } from '@telecom/shared/services/header-service/header-service';
import type { GoogleTagManagerService } from '../google-tag-manager-service';

/**
 * Google Tag Manager Service 实现
 *
 * 在海外环境中动态注入 GTM 代码
 */
@injectable()
export class GoogleTagManagerServiceImpl implements GoogleTagManagerService {
    private readonly _gtmId = 'GTM-KLS87JCT';
    private _initialized = false;

    @inject(HeaderService)
    private readonly _headerService!: HeaderService;

    @inject(Logger)
    private readonly _logger!: Logger;

    /**
     * 初始化 GTM
     * 只在海外环境中注入 GTM 代码
     */
    initialize(): void {
        // 防止重复初始化
        if (this._initialized) {
            this._logger.warn('[GTM] Already initialized');
            return;
        }

        // 只在海外环境注入
        if (!this._isOverseasRegion()) {
            this._logger.info('[GTM] Skipped - CN region detected');
            return;
        }

        this._logger.info('[GTM] Initializing for overseas region');

        this._injectHeadScript();
        this._injectBodyNoScript();

        this._initialized = true;
    }

    /**
     * 判断当前是否为海外环境
     * @returns true 表示海外环境，false 表示中国环境
     */
    private _isOverseasRegion(): boolean {
        const siteRegion = this._headerService.account?.siteRegion;

        // 如果 siteRegion 未定义，默认不加载 GTM（保守策略）
        if (!siteRegion) {
            this._logger.warn('[GTM] siteRegion is undefined, skipping GTM initialization');
            return false;
        }

        return siteRegion !== 'CN';
    }

    /**
     * 注入 GTM 脚本到 <head> 中
     */
    private _injectHeadScript(): void {
        const script = document.createElement('script');
        script.textContent = `
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${this._gtmId}');
        `.trim();

        // 插入到 head 的最前面
        const firstChild = document.head.firstChild;
        if (firstChild) {
            document.head.insertBefore(script, firstChild);
        } else {
            document.head.appendChild(script);
        }

        this._logger.info('[GTM] Head script injected');
    }

    /**
     * 注入 GTM noscript 到 <body> 开始位置
     */
    private _injectBodyNoScript(): void {
        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${this._gtmId}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';

        noscript.appendChild(iframe);

        // 插入到 body 的最前面
        const firstChild = document.body.firstChild;
        if (firstChild) {
            document.body.insertBefore(noscript, firstChild);
        } else {
            document.body.appendChild(noscript);
        }

        this._logger.info('[GTM] Body noscript injected');
    }
}
